import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { UserService } from '../../../shared/services/features/user';
import { AuthService } from '../../../shared/services/features/oauth/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { combineLatest } from 'rxjs/internal/observable/combineLatest';
import { BehaviorSubject, from, merge, Observable, ReplaySubject, SubscriptionLike, zip } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { NEVER } from 'rxjs/internal/observable/never';
import { Metas, SeoService } from '../../../shared/services/utilities/seo.service';
import { KazaplanService } from '../kazaplan.service';
import { ToastrService } from '../../../shared/services/utilities/toastr.service';
import { MISC_CONST } from '../../../utils/const/misc';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { KazaplanAction } from '../kazaplan-options-v2/kazaplan-option-actions';
import { AppDataLayer, DataLayerService } from '../../../shared/services/utilities/data-layer.service';
import { kazaplanMediaSlug, kazaplanViewSlug, mediaIdSlug, planIdSlug } from '../kazaplan-routing.module';
import { filterUndefined, KazaService } from '@adeo/ngx-kozikaza-api';
import { kazaplanSlug } from '../../../app-routing.module';
import { ModalService } from "../../../utils/modals/modals.service";
import { KzModal } from '../../../kz-ui/ui/modal/kz-modal';
import { KazaplanFullscreenPopinComponent } from './kazaplan-fullscreen-popin/kazaplan-fullscreen-popin.component';
import { ModalClosureType } from '../../../utils/modals/modals-closure-types';
import { PlanMediaType } from '../../../shared/models/kaza/plan';
import { UserStoreService } from '../../../shared/services/state-management/user-store.service';
import { KazaPlanService, Media, Plan } from '@adeo/ngx-kozikaza-api/kazaplan';

@Injectable()
export class KazaplanFullscreenService {
  static defaultModalConfig = {
    kaza: null,
    nbKazaplanOfKaza: null,
    modeLight: null,
    isMainPlan: null,
    isOwner: null,
    isAdmin: null,
    featuredMedias: null,
    filterMedias: null,
    planMediaId: null
  };

  private fetchingKaza = false;
  private _nbKazaplanOfKaza: number;
  private _nbPlanMedias = 0;
  private _hasMoreMedias = false;
  private previousMetas: Metas = null;
  private _planMediaFilter: PlanMediaFilter = { page: 1, mediaFilter: null };
  private atLeastOnPlanMediaHasBeenDeleted = false;
  private _displayCurrentKazaplan: boolean = null;
  private $currentKazaplan: ReplaySubject<Plan> = new ReplaySubject<Plan>(1);
  private isModalMode = false;
  private firstMediaDisplayed = true;
  // $displayCurrentKazaplan values :
  // null : has not been opened yet
  // true : opened
  // false : closed but already been opened
  private $displayCurrentKazaplan: BehaviorSubject<
    boolean
  > = new BehaviorSubject<boolean>(null);
  private $KazaplanMedias: BehaviorSubject<Media[]> = new BehaviorSubject<
    Media[]
  >(null);
  private $KazaplanFeaturedMedia: BehaviorSubject<Media> = new BehaviorSubject<
    Media
  >(null);
  private $KazaplanCurrentMedia: BehaviorSubject<number> = new BehaviorSubject<
    number
  >(null);
  private $NbKazaplanMedias: BehaviorSubject<number> = new BehaviorSubject<
    number
  >(null);
  private $KazaplanLocationState: BehaviorSubject<string> = new BehaviorSubject<
    string
  >(null);
  private $KazaplanPlanMediaFilter: BehaviorSubject<
    PlanMediaFilter
  > = new BehaviorSubject<PlanMediaFilter>(this._planMediaFilter);
  private $modeLight: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );

  private previousDataLayer: AppDataLayer;
  private closedFromBackButton = false;

  private locationSubscription: SubscriptionLike;

  constructor(
    private _modalService: ModalService,
    private userService: UserService,
    private kazaService: KazaService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private location: Location,
    private seoService: SeoService,
    private kazaplanService: KazaplanService,
    private readonly localizeRouterService: LocalizeRouterService,
    private dataLayerService: DataLayerService,
    private readonly userStoreService: UserStoreService,
    private kazaPlanService: KazaPlanService
  ) {}
  /*
   * Modal
   * */
  openModal(
    plan?: Plan,
    config = KazaplanFullscreenService.defaultModalConfig
  ): Observable<KzModal<KazaplanFullscreenPopinComponent>> {
    // Set Plan and his Kaza to prevent fetching
    if (plan) {
      this.currentKazaplan = plan;
    }
    // Set nb plan of Kaza to prevent fetching
    if (config.nbKazaplanOfKaza !== undefined) {
      this._nbKazaplanOfKaza = config.nbKazaplanOfKaza;
    }
    this.modeLight = config.modeLight ? config.modeLight : false;

    this.closedFromBackButton = false;
    this.locationSubscription = this.location.subscribe(
      (value: PopStateEvent) => {
        // close modal on browser back button action
        this.closedFromBackButton = true;
      }
    );

    const previousURL = this.location.path();
    this.previousDataLayer = {
      ...this.dataLayerService.getCurrentAppDataLayer()
    };
    return this.getCurrentKazaplan().pipe(
      map(p => {
        plan = p;
        const modal = this._modalService.open<KazaplanFullscreenPopinComponent>(
          KazaplanFullscreenPopinComponent,
          {
            size: "fullscreen"
          }
        );
        modal.componentRef.instance.inputData = Object.assign(
          { plan: p },
          config
        );
        return modal;
      }),
      switchMap(modal => {
        return merge(
          modal.afterOpened().pipe(
            withLatestFrom(this.seoService.currentMetas),
            tap(([result, currentMetas]) => {
              this.previousMetas = currentMetas;
              // Metas are updated on kazaplanSetCurrentMedia function
            }),
            map(afterOpened => {
              // set default route, only if there is not locationState modification yet
              const locationState = this.$KazaplanLocationState.getValue();
              if (locationState === null) {
                this.setKazaplanRoute(plan.id, null);
              }
              return modal;
            })
          ),
          modal.beforeClosed().pipe(
            map(result => {
              this.kazaplanLocationState = null;
              if (result || this.closedFromBackButton) {
                if (this.previousMetas) {
                  this.seoService.setMetas(this.previousMetas);
                  this.previousMetas = null;
                }
                if (
                  this.previousDataLayer &&
                  result &&
                  result.closureType &&
                  (result.closureType === ModalClosureType.Cross ||
                    result.closureType === ModalClosureType.Overlay)
                ) {
                  this.dataLayerService.setCurrentAppDataLayer(
                    this.previousDataLayer
                  );
                }
                this.setPlanMedias([]);
                // init vars
                this.initVars();
                this.initVarsMedias();
                // if no action has been already setted, and if atLeastOnPlanMediaHasBeenDeleted
                if (
                  this.atLeastOnPlanMediaHasBeenDeleted &&
                  result.result &&
                  !result.result.action
                ) {
                  result.result.action = KazaplanAction.MediaListUpdated;
                }
                this.location.go(previousURL);
              }
              this.isModalMode = false;
              if (
                this.locationSubscription &&
                !this.locationSubscription.closed
              ) {
                this.locationSubscription.unsubscribe();
              }
              return modal;
            })
          )
        );
      })
    );
  }

  /*
   * Init Vars
   * */
  private initVars(): void {
    this.$currentKazaplan = new ReplaySubject<Plan>(1);
  }

  private initVarsMedias(): void {
    this._planMediaFilter = { page: 1, mediaFilter: null };
    this.$KazaplanPlanMediaFilter = new BehaviorSubject<PlanMediaFilter>(
      this._planMediaFilter
    );
    this.$KazaplanMedias = new BehaviorSubject<Media[]>(null);
    this.$NbKazaplanMedias = new BehaviorSubject<number>(null);
    this._nbPlanMedias = 0;
  }

  /*
   * Current Kazaplan
   * */
  public getCurrentKazaplan(): Observable<Plan> {
    return this.$currentKazaplan.asObservable().pipe(filter(k => k !== null));
  }

  public set currentKazaplan(k: Plan) {
    this.$currentKazaplan.next(k);
  }
  /*
   * Mode Light
   * */
  public getModeLight(): Observable<boolean> {
    return this.$modeLight.asObservable().pipe(filter(mode => mode !== null));
  }

  public set modeLight(mode: boolean) {
    this.$modeLight.next(mode);
  }

  /*
   * Plan infos
   * */
  public isOwner(): Observable<boolean> {
    return combineLatest([
      this.getCurrentKazaplan(),
      this.userStoreService.userStore.pipe(filterUndefined())
    ]).pipe(
      switchMap(([plan, user]) =>
        user ? of(`${plan.userId}` == user.kazaplanId) : of(false)
      )
    );
  }

  /*
   * Plan Medias
   * */
  public getKazaplanCurrentMedia(): Observable<number> {
    return this.$KazaplanCurrentMedia.pipe(filter(cm => cm >= -1));
  }

  public set kazaplanCurrentMedia(currentIndex: number) {
    zip(
      this.$currentKazaplan,
      this.$KazaplanMedias.pipe(filter(m => m !== null))
    )
      .pipe(
        take(1),
        tap(([plan, medias]) => {
          this.setKazaplanRoute(
            plan.id,
            medias && medias[currentIndex]
              ? `/${kazaplanMediaSlug}/` + medias[currentIndex].id
              : null
          );
          if (!this.isModalMode && this.firstMediaDisplayed) {
            // if first access from KazaplanfullScreen Page, metas and GA has already been setted by routing
            return;
          }
          // update Metas & Datalayer (GA)
          if (medias && medias[currentIndex]) {
            const newMetas = this.kazaplanService.getKazaplanMetas(
              plan,
              medias[currentIndex]
            );
            this.seoService.setMetas(newMetas);
            const template = `/${kazaplanSlug}/${kazaplanViewSlug}/:${planIdSlug}/${kazaplanMediaSlug}/:${mediaIdSlug}`;
            this.dataLayerService.sendPageView({
              template: `${template}`,
              title: newMetas.title,
              params: {
                [planIdSlug]: plan.id,
                [mediaIdSlug]: medias[currentIndex].id
                  ? medias[currentIndex].id
                  : "default_thumbnail"
              }
            });
          }
        })
      )
      .subscribe(() => {
        this.firstMediaDisplayed = false;
        if (currentIndex >= -1) {
          this.displayCurrentKazaplan =
            this._displayCurrentKazaplan === null ? null : false;
          this.$KazaplanCurrentMedia.next(currentIndex);
        }
      });
  }

  public previousKazaplanMedia() {
    return zip(this.$KazaplanMedias, this.getKazaplanCurrentMedia()).pipe(
      tap(([medias, currentMedia]) => {
        this.kazaplanCurrentMedia =
          currentMedia === 0 ? medias.length - 1 : currentMedia - 1;
      })
    );
  }

  public nextKazaplanMedia() {
    return zip(this.$KazaplanMedias, this.getKazaplanCurrentMedia()).pipe(
      tap(([medias, currentMedia]) => {
        if (currentMedia >= medias.length - 5 && this._hasMoreMedias) {
          this.nextPlanMediaFilter();
        }
        this.kazaplanCurrentMedia =
          currentMedia === medias.length - 1 ? 0 : currentMedia + 1;
      })
    );
  }

  public get nbPlanMedias(): Observable<number> {
    return this.$NbKazaplanMedias
      .asObservable()
      .pipe(filter(nb => nb !== null));
  }

  public setNbKazaplanMedias(nb: number): void {
    this._nbPlanMedias = nb;
    this.$NbKazaplanMedias.next(this._nbPlanMedias);
  }

  public initPlanMedias(featuredMediaId?: string): Observable<Media[]> {
    return this.filterPlanMedias(null, featuredMediaId);
  }

  public get planMediasFilter(): Observable<PlanMediaFilter> {
    return this.$KazaplanPlanMediaFilter.asObservable();
  }

  public setPlanMediasFilter(planMediaFilter: PlanMediaFilter): void {
    this._planMediaFilter = planMediaFilter;
    if (this._planMediaFilter.page === 1) {
      this.setPlanMedias([]);
    }
    this.$KazaplanPlanMediaFilter.next(planMediaFilter);
  }

  public nextPlanMediaFilter(): void {
    this._planMediaFilter = {
      page: this._planMediaFilter.page + 1,
      mediaFilter: this._planMediaFilter.mediaFilter
    };
    this.$KazaplanPlanMediaFilter.next(this._planMediaFilter);
  }

  public filterPlanMedias(
    mediaPlanFilter: string,
    featuredMediaId?: string
  ): Observable<Media[]> {
    this._hasMoreMedias = false;
    return combineLatest([
      this.getCurrentKazaplan().pipe(
        distinctUntilChanged((prev, curr) => prev.id !== curr.id)
      ),
      this.planMediasFilter
    ]).pipe(
      switchMap(([kazaplan, triggerMediaFilter]) => {
        const page = triggerMediaFilter.page;
        const args = {
          page: triggerMediaFilter.page,
          pageSize: MISC_CONST.NB_MEDIAS_OFFSET,
          filters: []
        };
        args.filters = [
          {
            key: 'media_available',
            operator: '$eq',
            value: [true] as any
          }
        ];
        const mediaFilter = mediaPlanFilter
          ? mediaPlanFilter
          : triggerMediaFilter.mediaFilter;
        if (mediaFilter) {
          args.filters = [
             {
             key:'type_id',
             operator:'$eq',
             value:[mediaFilter]
            },
            {
              key:'media_available',
              operator:'$eq',
              value:[true] as any
            }
          ];
        }

        if (
          mediaFilter === PlanMediaType.CAPTURE_3D ||
          mediaFilter === PlanMediaType.CAPTURE_2D
        ) {
          args.filters = [
             {
              key:'type_id',
              operator:'$eq',
              value:[PlanMediaType.CAPTURE_AUTO]
            },
            {
              "key": "metadata->>'type'",
              "operator": "$eqjsonb",
               value: (mediaFilter === PlanMediaType.CAPTURE_3D) ? ['3d'] : ['2d']
            }
          ];
        }
        return this.kazaPlanService.getMediasByPlanId(kazaplan?.id?.toString(),args.page,args.pageSize,{filters:args.filters}).pipe(
          withLatestFrom(this.$KazaplanMedias),
          switchMap(([medias, currentMediasList]: any) => {
            this.setNbKazaplanMedias(medias.total ? medias.total : 0);
            let mediasReturned = [
              ...(currentMediasList ? currentMediasList : []),
              ...medias.data
            ];
            this._hasMoreMedias = mediasReturned.length < this._nbPlanMedias;
            this.setPlanMedias(mediasReturned);
            if (page === 1) {
              const featuredMediaIndex = mediasReturned.findIndex(
                media => media.id === featuredMediaId
              );
              if (featuredMediaId && featuredMediaIndex === -1) {
                this.router.navigate(
                  this.localizeRouterService.translateRoute([
                    "/not_found"
                  ]) as string[],
                  { queryParams: { from: this.router.url } }
                );
              }
              featuredMediaId = null;
              this.kazaplanCurrentMedia =
                featuredMediaIndex > -1 ? featuredMediaIndex : 0;
            }
            return of(mediasReturned);
          })
        );
      })
    );
  }

  public deleteMedia(mediaId: string, planId: number): Observable<any> {
    return this.getCurrentKazaplan().pipe(
      switchMap(kazaplan => {
        return this._modalService
          .openYesNoDialogOnClosed({
            title: $localize`:@@popin.yesNoDialog.title.removePlanMedia:Delete the media`,
            content: $localize`:@@popin.yesNoDialog.content.removePlanMedia:Are you sure you want to delete the media ?`
          })
          .pipe(
            switchMap(() => {
              return from(
                this.kazaPlanService.updateMedia(mediaId, {
                  deletedAt: new Date() as any
                })
              ).pipe(
                //toBeFixed
                withLatestFrom(this.planMedias),
                switchMap(([deleteMediaREsult, planMedias]) => {
                  const updatedList = planMedias.filter(
                    media => media.id !== mediaId
                  );
                  this.setPlanMedias(updatedList);
                  this.setNbKazaplanMedias(updatedList.length);
                  this.kazaplanCurrentMedia = 0;
                  this.atLeastOnPlanMediaHasBeenDeleted = true;
                  return of(deleteMediaREsult);
                }),
                tap(() =>
                  this.toastrService.info(
                    $localize`:@@toast.plan_media_deleted:Media has been deleted`
                  )
                ),
                catchError((err, obs) => {
                  this.toastrService.error(
                    $localize`:@@toast.plan_media_deleting_error:An error has occurred. The media could not be deleted.`
                  );
                  return NEVER;
                })
              );
            })
          );
      })
    );
  }

  public get planMedias(): Observable<Array<Media>> {
    return this.$KazaplanMedias.pipe(filter(v => v !== null));
  }

  public setPlanMedias(medias: Media[]): void {
    this.$KazaplanMedias.next(medias);
  }

  public getDisplayCurrentKazaplan(): Observable<boolean> {
    return this.$displayCurrentKazaplan.asObservable();
  }

  public set displayCurrentKazaplan(display: boolean) {
    if (display) {
      this.kazaplanCurrentMedia = -1;
    }
    this._displayCurrentKazaplan = display;
    this.$displayCurrentKazaplan.next(display);
  }

  /*
   * Plan Featured Media
   * */
  public getPlanFeaturedMedia(): Observable<Media> {
    return this.$KazaplanFeaturedMedia.pipe(filter(v => v !== null));
  }

  public set planFeaturedMedia(featuredPlanMedia: Media) {
    this.$KazaplanCurrentMedia.next(-1);
    this.$KazaplanFeaturedMedia.next(featuredPlanMedia);
  }

  /*
   * Plan routes
   * */
  private getKazaplanRoute(planID: number): string {
    return (this.localizeRouterService.translateRoute([
      "kazaplan",
      "view",
      planID.toString()
    ]) as string[]).join("/");
  }

  private get kazaplanTemplate(): string {
    // todo : with i18n, replace '/kazaplan/view/' with this.localizeRouterService.translateRoute(path)as string[]
    return `/${kazaplanSlug}/${kazaplanViewSlug}/`;
  }

  public setKazaplanRoute(planID: number, suffix?: string): void {
    const hasLocationState = !!this.$KazaplanLocationState.getValue();
    const locationState =
      this.getKazaplanRoute(planID) + (suffix ? suffix : "");
    !hasLocationState
      ? this.location.go(locationState)
      : this.location.replaceState(locationState);
    this.kazaplanLocationState = locationState;
  }

  /*
   * Plan Location State
   * */
  public getKazaplanLocationState(): Observable<string> {
    return this.$KazaplanLocationState.asObservable();
  }

  public set kazaplanLocationState(locationState: string) {
    this.$KazaplanLocationState.next(locationState);
  }
}

export class PlanMediaFilter {
  public page = 1;
  public mediaFilter: PlanMediaType = null;
}

